import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UtensilsCrossed, Star, Heart, MapPin, Clock, Navigation } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSavedRestaurants } from "@/hooks/useSavedRestaurants";
import { useNavigate } from "react-router-dom";
import { SignupNudge } from "./SignupNudge";

interface Restaurant {
  id: number;
  name: string;
  category: string;
  terminal: string;
  hours: string;
  location: string;
  rating?: number;
  userRatingsTotal?: number;
  url: string;
}

interface RestaurantPreviewProps {
  airportCode: string;
  airportName: string;
}

export const RestaurantPreview = ({ airportCode, airportName }: RestaurantPreviewProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isSaved, toggleSave } = useSavedRestaurants();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [savedOnly, setSavedOnly] = useState(false);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        const { data, error: fnError } = await supabase.functions.invoke("get-restaurants", {
          body: { airport: airportCode, terminal: "A" },
        });
        if (fnError) throw fnError;
        if (data?.restaurants) {
          setRestaurants(data.restaurants);
        }
      } catch (err) {
        console.error("Error fetching restaurants:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurants();
  }, [airportCode]);

  // Don't show a broken card: hide the section entirely when there's nothing to show.
  if (!loading && (error || restaurants.length === 0)) return null;

  const categories = ["all", ...Array.from(new Set(restaurants.map((r) => r.category)))];
  const byCategory = selectedCategory === "all" ? restaurants : restaurants.filter((r) => r.category === selectedCategory);
  const filtered = savedOnly ? byCategory.filter((r) => isSaved(airportCode, r.id)) : byCategory;

  return (
    <Card className="shadow-md">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className="text-lg font-semibold">Dining at {airportCode}</h3>
        </div>

        {/* Category filter */}
        {!loading && restaurants.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px] ${
                  selectedCategory === cat
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
              >
                {cat === "all" ? "All" : cat}
              </button>
            ))}
            {user && (
              <button
                onClick={() => setSavedOnly((v) => !v)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors min-h-[32px] ${
                  savedOnly
                    ? "bg-red-500 text-white"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                }`}
                aria-pressed={savedOnly}
              >
                <Heart className={`h-3 w-3 ${savedOnly ? "fill-current" : ""}`} aria-hidden="true" />
                Saved
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded-lg border border-border animate-pulse">
                <div className="h-4 w-28 rounded bg-muted skeleton-shimmer mb-2" />
                <div className="h-3 w-20 rounded bg-muted skeleton-shimmer" />
              </div>
            ))}
          </div>
        ) : error || restaurants.length === 0 ? (
          <p className="text-center py-6 text-sm text-muted-foreground">
            {error ? "Unable to load restaurant data right now." : "No restaurant data available."}
          </p>
        ) : (
          <>
            {filtered.length === 0 && savedOnly && (
              <p className="text-center py-6 text-sm text-muted-foreground">
                No saved restaurants here yet. Tap the heart on a card to save one.
              </p>
            )}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filtered.slice(0, 8).map((restaurant) => {
                const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  `${restaurant.name} ${airportName} ${airportCode} airport`
                )}`;
                return (
                  <div
                    key={restaurant.id}
                    className="p-3 rounded-lg border border-border hover:border-primary/30 transition-colors"
                  >
                    {/* Header: name + category */}
                    <div className="flex items-start justify-between gap-2 min-w-0">
                      <a
                        href={restaurant.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-sm hover:text-primary transition-colors truncate min-w-0 flex-1"
                      >
                        {restaurant.name}
                      </a>
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        {restaurant.category}
                      </Badge>
                    </div>

                    {/* Meta: terminal · location · hours · rating — wraps cleanly */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                      {restaurant.terminal && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">Terminal {restaurant.terminal}</span>
                        </span>
                      )}
                      {restaurant.location && restaurant.location !== restaurant.terminal && (
                        <span className="truncate min-w-0 max-w-full">{restaurant.location}</span>
                      )}
                      {restaurant.hours && (
                        <span
                          className={`inline-flex items-center gap-1 ${
                            restaurant.hours === "Open Now" ? "text-success font-medium" : ""
                          }`}
                        >
                          <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">{restaurant.hours}</span>
                        </span>
                      )}
                      {restaurant.rating && (
                        <span className="inline-flex items-center gap-1">
                          <Star className="h-3 w-3 text-warning fill-warning shrink-0" aria-hidden="true" />
                          {restaurant.rating}
                        </span>
                      )}
                    </div>

                    {/* Action row: Get directions + Save */}
                    <div className="flex items-center justify-between gap-2 mt-2.5">
                      <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors min-h-[32px]"
                        aria-label={`Get directions to ${restaurant.name}`}
                      >
                        <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
                        Get directions
                      </a>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!user) {
                            navigate("/auth");
                            return;
                          }
                          toggleSave({
                            airportCode,
                            restaurantId: restaurant.id,
                            restaurantName: restaurant.name,
                            category: restaurant.category,
                            terminal: restaurant.terminal,
                            url: restaurant.url,
                          });
                        }}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-secondary transition-colors shrink-0"
                        aria-label={
                          isSaved(airportCode, restaurant.id)
                            ? `Remove ${restaurant.name} from saved`
                            : `Save ${restaurant.name}`
                        }
                        aria-pressed={isSaved(airportCode, restaurant.id)}
                      >
                        <Heart
                          className={`h-4 w-4 transition-colors ${
                            isSaved(airportCode, restaurant.id)
                              ? "fill-red-500 text-red-500"
                              : "text-muted-foreground"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!user && (
              <SignupNudge
                icon={<Heart className="h-4 w-4" />}
                message="Sign up to save favorites and get dining recommendations"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
